/**
 * Logs UI model helpers and status constants.
 * Production data comes from GET /counselor/parent-email-delivery-logs via adapter.
 */

import { CONTACT_TYPE_LABELS } from './parentNotificationData'

export { CONTACT_TYPE_LABELS }

export const LOG_STATUSES = {
	SENT: 'sent',
	FAILED: 'failed',
	SKIPPED: 'skipped',
	PARTIAL: 'partial',
}

export const LOG_STATUS_LABELS = {
	[LOG_STATUSES.SENT]: 'Sent',
	[LOG_STATUSES.FAILED]: 'Failed',
	[LOG_STATUSES.SKIPPED]: 'Skipped',
	[LOG_STATUSES.PARTIAL]: 'Partial',
}

/** Statuses used by quick filters and status drawer (real API values only). */
export const LOG_FILTER_STATUSES = [
	LOG_STATUSES.SENT,
	LOG_STATUSES.FAILED,
	LOG_STATUSES.SKIPPED,
]

export const STATUS_PILL_TONE = {
	[LOG_STATUSES.SENT]: 'delivered',
	[LOG_STATUSES.FAILED]: 'failed',
	[LOG_STATUSES.SKIPPED]: 'queued',
	[LOG_STATUSES.PARTIAL]: 'required',
}

/** Fallback notification labels by preference code (aligned with backend seed). */
export const NOTIFICATION_BY_CODE = {
	essay_updates: { code: 'essay_updates', name: 'Essay updates' },
	deadline_alerts: { code: 'deadline_alerts', name: 'Deadline alerts' },
	application_milestones: {
		code: 'application_milestones',
		name: 'Application milestones',
	},
	test_scores: { code: 'test_scores', name: 'Test scores' },
	general_updates: { code: 'general_updates', name: 'General updates' },
}

export const getLogStatusCounts = (events = []) => {
	const counts = {
		all: events.length,
		[LOG_STATUSES.SENT]: 0,
		[LOG_STATUSES.FAILED]: 0,
		[LOG_STATUSES.SKIPPED]: 0,
		[LOG_STATUSES.PARTIAL]: 0,
	}

	events.forEach(event => {
		if (counts[event.status] != null) counts[event.status] += 1
	})

	return counts
}

export const formatRecipientsSummary = recipients => {
	const list = Array.isArray(recipients) ? recipients : []
	if (list.length === 0) return { names: '—', countLabel: '0 recipients' }
	if (list.length === 1) {
		return {
			names: list[0].name || list[0].email || '—',
			countLabel: '1 recipient',
		}
	}

	const first = list[0]?.name || list[0]?.email || ''
	const second = list[1]?.name || list[1]?.email || ''
	const firstName = first.split(' ')[0] || first
	const secondName = second.split(' ')[0] || second
	const extra = list.length - 2

	return {
		names:
			extra > 0
				? `${firstName}, ${secondName} +${extra}`
				: `${firstName}, ${secondName}`,
		countLabel: `${list.length} recipients`,
	}
}

export const formatLogDateTime = value => {
	if (!value) return '—'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return '—'

	const datePart = date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
	const timePart = date.toLocaleString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})

	return `${datePart} ${timePart}`
}

export const getUniqueStudentsFromLogs = (events = []) => {
	const map = new Map()
	events.forEach(event => {
		if (event?.student?.id) map.set(event.student.id, event.student)
	})
	return Array.from(map.values()).sort((a, b) =>
		String(a.name).localeCompare(String(b.name)),
	)
}

/**
 * Unique recipients across log events.
 * When studentIds is non-empty, only recipients from those students are included.
 */
export const getUniqueRecipientsFromLogs = (events = [], studentIds = []) => {
	const selectedStudents = Array.isArray(studentIds) ? studentIds : []
	const map = new Map()

	events.forEach(event => {
		if (
			selectedStudents.length &&
			!selectedStudents.includes(event?.student?.id)
		) {
			return
		}

		;(event.recipients || []).forEach(recipient => {
			if (!recipient?.id || map.has(recipient.id)) return
			map.set(recipient.id, {
				id: recipient.id,
				name: recipient.name,
				email: recipient.email,
				type: recipient.type,
			})
		})
	})

	return Array.from(map.values()).sort((a, b) =>
		String(a.name || a.email || '').localeCompare(
			String(b.name || b.email || ''),
		),
	)
}
